import React, { useState, useEffect } from 'react'
import { getCFBScorecard, getCFBValidation } from './api'

// Public CFB track-record page — the shareable proof link (/cfb/proof). Bypasses the
// age + access gates and reads only aggregate, settled data: the live season scorecard
// (record + per-market hit %) and the backtest validation scorecard (per-market
// calibration + verdict). No per-play data, no picks — just the receipts.

const T = {
  bg: '#05090f', card: '#0F1520', card2: '#0b1119', border: '#22303f',
  text: '#eaf3ec', muted: '#8ea3b0', faint: '#5f7382', green: '#00D97E', green2: '#2fe37a',
  amber: '#e3c02f', red: '#ff7a7a',
  mono: 'ui-monospace, SFMono-Regular, Menlo, monospace',
}
const pctInt = p => (p == null ? '—' : `${Math.round(p * 100)}%`)
const LABEL = { pass_yards: 'Passing Yards', rush_yards: 'Rushing Yards', rec_yards: 'Receiving Yards',
  receptions: 'Receptions', anytime_td: 'Anytime TD' }
const VERDICT_COLOR = { PASS: T.green2, REVIEW: T.amber, BUILDING: T.faint }

function Stat({ label, value, color }) {
  return (
    <div>
      <div style={{ fontSize: 10, color: T.faint, textTransform: 'uppercase', letterSpacing: '.05em' }}>{label}</div>
      <div style={{ fontFamily: T.mono, fontWeight: 800, fontSize: 22, color: color || T.text }}>{value}</div>
    </div>
  )
}

export default function CFBProofPage() {
  const season = 2025
  const [card, setCard] = useState(null)
  const [val, setVal] = useState(null)
  useEffect(() => {
    getCFBScorecard(season).then(setCard)
    getCFBValidation().then(setVal)
  }, [])

  const o = card?.overall || {}
  const wl = o.graded ? `${o.wins || 0}-${o.losses || 0}${o.pushes ? `-${o.pushes}` : ''}` : '—'
  const byProp = card?.by_prop || {}
  const markets = val?.markets || {}

  return (
    <div style={{ minHeight: '100vh', background: T.bg, color: T.text,
      fontFamily: 'system-ui, -apple-system, sans-serif', padding: '0 0 48px' }}>
      <div style={{ maxWidth: 640, margin: '0 auto', padding: '20px 16px' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
          <span style={{ fontWeight: 900, fontSize: 22, letterSpacing: '-0.03em' }}>
            LINE<span style={{ color: T.green2 }}>BRKR</span>
          </span>
          <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: '.06em', color: '#04140D',
            background: T.green2, borderRadius: 6, padding: '3px 7px' }}>CFB · PROOF</span>
        </div>
        <div style={{ fontSize: 13, color: T.muted, marginBottom: 18 }}>
          College-football track record — settled results only. Research, not betting advice · 21+
        </div>

        {/* Season record */}
        <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 12,
          padding: '16px 18px', display: 'flex', gap: 26, marginBottom: 16 }}>
          <Stat label={`${season} Record`} value={wl} />
          <Stat label="Hit %" value={pctInt(o.win_pct)} color={T.green} />
          <Stat label="Graded" value={o.graded || 0} />
        </div>

        {/* Per-market (live season) */}
        {Object.keys(byProp).length > 0 && (
          <>
            <SectionTitle>This season, by market</SectionTitle>
            <Table rows={Object.entries(byProp).map(([k, v]) => ({
              label: LABEL[k] || k, a: v.graded ? `${v.wins}-${v.losses}` : '—',
              b: pctInt(v.win_pct), bColor: T.green,
            }))} colA="W-L" colB="Hit %" />
          </>
        )}

        {/* Validation / backtest trust scorecard */}
        <SectionTitle>Backtest validation {val?.seasons?.length ? `(${val.seasons.join(', ')})` : ''}</SectionTitle>
        <div style={{ fontSize: 12, color: T.faint, marginBottom: 8, lineHeight: 1.5 }}>
          Each market replayed week-by-week on completed seasons (walk-forward, no leakage).
          Calibration error = how far predicted % sat from actual. Verdict: PASS &lt; 5pp · REVIEW · BUILDING (thin).
        </div>
        {Object.keys(markets).length === 0 ? (
          <div style={{ color: T.muted, fontSize: 13, padding: '18px 4px' }}>
            No backtest on record yet — connect the data feed and run validation.
          </div>
        ) : (
          <Table rows={Object.entries(markets).map(([k, c]) => ({
            label: LABEL[k] || k,
            a: c.plays ? `${c.hit_rate}%` : '—',
            b: c.cal_err != null ? `${c.cal_err}pp` : '—',
            c: c.verdict, cColor: VERDICT_COLOR[c.verdict] || T.faint,
            sub: `${c.plays || 0} plays`,
          }))} colA="Hit %" colB="Cal err" colC="Verdict" />
        )}

        <div style={{ marginTop: 24, textAlign: 'center' }}>
          <a href="/?sport=cfb" style={{ fontSize: 13, fontWeight: 700, color: T.green2, textDecoration: 'none' }}>
            See this week's board →
          </a>
        </div>
      </div>
    </div>
  )
}

function SectionTitle({ children }) {
  return <div style={{ fontSize: 13, fontWeight: 800, color: T.text, margin: '18px 0 8px',
    letterSpacing: '-0.01em' }}>{children}</div>
}

function Table({ rows, colA, colB, colC }) {
  return (
    <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 12, overflow: 'hidden' }}>
      <div style={{ display: 'flex', padding: '8px 14px', fontSize: 10, color: T.faint,
        textTransform: 'uppercase', letterSpacing: '.05em', borderBottom: `1px solid ${T.border}` }}>
        <span style={{ flex: 1 }}>Market</span>
        <span style={{ width: 64, textAlign: 'right' }}>{colA}</span>
        <span style={{ width: 64, textAlign: 'right' }}>{colB}</span>
        {colC && <span style={{ width: 78, textAlign: 'right' }}>{colC}</span>}
      </div>
      {rows.map((r, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', padding: '10px 14px',
          borderTop: i ? `1px solid ${T.border}` : 'none' }}>
          <span style={{ flex: 1 }}>
            <span style={{ fontSize: 13.5, fontWeight: 600 }}>{r.label}</span>
            {r.sub && <span style={{ fontSize: 10.5, color: T.faint, marginLeft: 6 }}>{r.sub}</span>}
          </span>
          <span style={{ width: 64, textAlign: 'right', fontFamily: T.mono, fontSize: 13,
            color: r.bColor && colB === 'Hit %' ? r.bColor : T.text }}>{r.a}</span>
          <span style={{ width: 64, textAlign: 'right', fontFamily: T.mono, fontSize: 13, color: T.muted }}>{r.b}</span>
          {colC && <span style={{ width: 78, textAlign: 'right', fontSize: 11, fontWeight: 800,
            color: r.cColor }}>{r.c}</span>}
        </div>
      ))}
    </div>
  )
}
